import { useState } from "react";
import { api } from "@/lib/api";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import i18n from "@/i18n";

/**
 * One-click PDF download for any /reports/{kind}/pdf endpoint.
 * Sends the same filters currently shown on screen so the PDF is
 * guaranteed to match the visible data.
 */
export default function DownloadPdfButton({ endpoint, params = {}, filename,
                                              label }) {
    const [loading, setLoading] = useState(false);

    const trigger = async () => {
        setLoading(true);
        try {
            const res = await api.get(endpoint, {
                params,
                responseType: "blob",
                headers: { Accept: "application/pdf" },
            });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            // Prefer the server-provided filename from Content-Disposition.
            const disp = res.headers?.["content-disposition"] || "";
            const match = /filename="?([^"]+)"?/.exec(disp);
            a.download = match ? match[1] : (filename || "MiseOne_Report.pdf");
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(i18n.t("download.pdfFailed", "Could not generate PDF"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <button type="button" onClick={trigger} disabled={loading}
            data-testid="download-pdf-button"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md
                       border border-neutral-300 text-xs font-semibold
                       text-neutral-800 hover:bg-neutral-50 disabled:opacity-60">
            {loading
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Download className="h-3.5 w-3.5" />}
            {label ?? i18n.t("download.pdf", "Download PDF")}
        </button>
    );
}
